import { type AirlineListItem, type AirlineListQuery } from '@utazzo/shared';
import { UzBadge, UzStatusBadge } from '@utazzo/ui';
import React, { useMemo } from 'react';

import { listAirlines } from '../airlines.api';
import { AirlineFormDrawer } from '../components/AirlineFormDrawer';
import {
  useBulkDeleteAirlines,
  useBulkSetAirlinesStatus,
  useAirlinesList,
  useCountryOptions,
  useDeleteAirline,
} from '../use-airlines';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCanExportFinanceData, useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

const LOW_COST_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Low-cost only' },
  { value: 'false', label: 'Full-service' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'lowCost',
    label: 'Type',
    options: LOW_COST_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const AirlinesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canExportFinance = useCanExportFinanceData();
  const canDelete = me?.role === 'ADMIN';
  const deleteAirline = useDeleteAirline();
  const bulkDeleteMut = useBulkDeleteAirlines();
  const bulkSetStatusMut = useBulkSetAirlinesStatus();
  const countriesQuery = useCountryOptions();

  // Build a countryId → name lookup so the list column can resolve the label
  // client-side without embedding a full country object in every list row.
  const countryNameById = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    for (const c of countriesQuery.data ?? []) {
      map.set(c.id, c.name);
    }
    return map;
  }, [countriesQuery.data]);

  const columns: ListScreenColumns<AirlineListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<AirlineListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'iataCode',
        header: 'IATA / ICAO',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.iataCode}</span>
            {row.icaoCode && <span className="text-xs text-zinc-500">{row.icaoCode}</span>}
          </div>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => <span className="font-medium text-zinc-900">{row.name}</span>,
      },
      {
        id: 'country',
        header: 'Country',
        cell: (row) => (
          <span className="text-zinc-600">
            {row.countryId ? (countryNameById.get(row.countryId) ?? '-') : '-'}
          </span>
        ),
      },
      {
        id: 'isLowCost',
        header: 'Low-cost',
        width: '100px',
        cell: (row) =>
          row.isLowCost ? (
            <UzBadge tone="secondary">Yes</UzBadge>
          ) : (
            <span className="text-zinc-400">-</span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<AirlineListItem, AirlineListQuery>
      title="Airlines"
      description="Carriers used in flight components and packages, keyed by IATA code."
      useList={useAirlinesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        const lowCost = state.filters.lowCost;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
          ...(lowCost === 'true' ? { isLowCost: true } : {}),
          ...(lowCost === 'false' ? { isLowCost: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add airline' }}
      renderDrawer={({ open, mode, row, close }) => (
        <AirlineFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          airline={row}
        />
      )}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} airline${rows.length === 1 ? '' : 's'}?`,
            description: 'These airlines will be soft-deleted and hidden from lists.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} airline${rows.length === 1 ? '' : 's'}?`,
            description: isActive
              ? 'They will appear in pickers and lists again.'
              : 'They will be hidden from pickers but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listAirlines(q),
        filename: 'airlines',
        canExport: canExportFinance,
        csv: [
          { header: 'IATA', value: (r) => r.iataCode },
          { header: 'ICAO', value: (r) => r.icaoCode ?? '' },
          { header: 'Name', value: (r) => r.name },
          {
            header: 'Country',
            value: (r) => (r.countryId ? (countryNameById.get(r.countryId) ?? '') : ''),
          },
          { header: 'Low-cost', value: (r) => (r.isLowCost ? 'Yes' : 'No') },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteAirline.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this airline?',
            description: `${row.name} (${row.iataCode}) will be soft-deleted and hidden from lists. Flight components using it are unaffected.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by IATA, ICAO, or name (min 3 characters)',
        emptyTitle: 'No airlines match these filters',
        emptyDescription: 'Adjust the type/status filter or clear the search box.',
        errorTitle: 'Failed to load airlines',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
